
import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import axios from "axios";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { Link } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import { baseUrl } from "../Univ.js";

// =========================================================
// HELPERS
// =========================================================

const getArray = (data) => {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.results)) return data.results;
    return [];
};

const normalize = (value) =>
    String(value ?? "").trim().toLowerCase();

const toNumber = (value) => {
    const number = Number(value ?? 0);
    return Number.isFinite(number) ? number : 0;
};

const formatAmount = (value) =>
    toNumber(value).toFixed(2);

const formatDate = (date) => {
    if (!date) return "";

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
};

// netamount may contain comma-separated amounts.
const getNetAmount = (value) => {
    if (value === null || value === undefined || value === "") {
        return 0;
    }

    if (typeof value === "string" && value.includes(",")) {
        return value
            .split(",")
            .reduce(
                (total, item) => total + toNumber(item.trim()),
                0
            );
    }

    return toNumber(value);
};

// =========================================================
// DEPARTMENT MAPPING
// item.sale_type contains the pharmacy department ID/name.
// =========================================================

const getSaleDepartment = (item, departments) => {
    const rawSaleType = item.sale_type;

    if (
        rawSaleType === undefined ||
        rawSaleType === null ||
        rawSaleType === ""
    ) {
        return "Unassigned";
    }

    if (typeof rawSaleType === "object") {
        const name =
            rawSaleType.department ??
            rawSaleType.name ??
            rawSaleType.label;

        if (name) return String(name);

        const found = departments.find(
            (department) =>
                Number(department.id) === Number(rawSaleType.id)
        );

        return found?.department || "Unassigned";
    }

    const saleType = String(rawSaleType).trim();

    const found = departments.find(
        (department) =>
            String(department.id) === saleType ||
            normalize(department.department) === normalize(saleType)
    );

    return found?.department || saleType;
};

// =========================================================
// CONSULTANT
// =========================================================

const getConsultant = (item) =>
    item.consultant_name ??
    item.consultant ??
    item.cons__consultant ??
    item.cons__name ??
    item.doctor_name ??
    item.doctor ??
    item.cons_name ??
    "";

// =========================================================
// FOOTER
// =========================================================

const GstReportFooter = ({ rows }) => {
    const totalDrugAmount = rows.reduce(
        (total, row) =>
            total +
            toNumber(
                row.discountedAmount ??
                row.gstTotal
            ),
        0
    );

    const totalDiscount = rows.reduce(
        (total, row) => total + toNumber(row.disc),
        0
    );

    const totalCGST = rows.reduce(
        (total, row) => total + toNumber(row.gstAmountTotal),
        0
    );

    const totalSGST = rows.reduce(
        (total, row) => total + toNumber(row.gstAmountTotal1),
        0
    );

    const totalNetAmount = rows.reduce(
        (total, row) => total + getNetAmount(row.netamount),
        0
    );

    const cellStyle = {
        padding: "10px 6px",
        fontWeight: 700,
        fontSize: "12px",
        overflow: "hidden",
        whiteSpace: "nowrap",
    };

    const columns = [
        120, 90, 140, 150, 100, 130, 200, 80, 110,
        90, 90, 120, 100, 100, 100, 150, 100,
    ];

    return (
        <div
            style={{
                display: "grid",
                gridTemplateColumns: columns.map((width) => `${width}px`).join(" "),
                minWidth: `${columns.reduce((sum, width) => sum + width, 0)}px`,
                alignItems: "center",
                borderTop: "2px solid #333",
                backgroundColor: "#f5f5f5",
            }}
        >
            {/* Date */}
            <div style={cellStyle}></div>

            {/* Time */}
            <div style={cellStyle}></div>

            {/* Sale Type */}
            <div style={cellStyle}></div>

            {/* Patient */}
            <div style={cellStyle}>TOTAL</div>

            {/* Mode */}
            <div style={cellStyle}></div>

            {/* Invoice */}
            <div style={cellStyle}></div>

            {/* Product */}
            <div style={cellStyle}></div>

            {/* Qty */}
            <div style={cellStyle}></div>

            {/* HSN */}
            <div style={cellStyle}></div>

            {/* CGST % */}
            <div style={cellStyle}></div>

            {/* SGST % */}
            <div style={cellStyle}></div>

            {/* Drug Amount */}
            <div style={cellStyle}>
                {formatAmount(totalDrugAmount)}
            </div>

            {/* Discount */}
            <div style={cellStyle}>
                {formatAmount(totalDiscount)}
            </div>

            {/* CGST */}
            <div style={cellStyle}>
                {formatAmount(totalCGST)}
            </div>

            {/* SGST */}
            <div style={cellStyle}>
                {formatAmount(totalSGST)}
            </div>

            {/* Net Amount */}
            <div style={cellStyle}>
                {formatAmount(totalNetAmount)}
            </div>

            {/* Cashier */}
            <div style={cellStyle}></div>
        </div>
    );
};

// =========================================================
// MAIN COMPONENT
// =========================================================

function DetailSalesGstReport() {
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);

    const [addedby, setAddedby] = useState("");
    const [mode, setMode] = useState("");
    const [department, setDepartment] = useState("all");

    const [rows, setRows] = useState([]);
    const [userData, setUserData] = useState([]);
    const [departments, setDepartments] = useState([]);

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");

    // =====================================================
    // LOAD CASHIERS AND PHARMACY DEPARTMENTS
    // =====================================================

    useEffect(() => {
        let cancelled = false;

        const loadDropdowns = async () => {
            try {
                const [
                    userResponse,
                    departmentResponse,
                ] = await Promise.all([
                    axios.get(`${baseUrl}user-search/`),
                    axios.get(`${baseUrl}pharmacy-departments/`),
                ]);

                if (cancelled) return;

                setUserData(getArray(userResponse.data));

                const departmentData = getArray(
                    departmentResponse.data
                );

                setDepartments(
                    departmentData
                        .map((item) => ({
                            id: item.id,
                            department:
                                item.department ??
                                item.name ??
                                item.label ??
                                "",
                        }))
                        .filter((item) => item.department)
                );
            } catch (err) {
                console.error("Dropdown loading error:", err);
            }
        };

        loadDropdowns();

        return () => {
            cancelled = true;
        };
    }, []);

    // =====================================================
    // FETCH GST SALES DATA
    // =====================================================

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        setError("");

        try {
            const params = {
                start_date: formatDate(startDate),
                end_date: formatDate(endDate),
                addedby,
                mode,
                status: 1,
            };

            const response = await axios.get(
                `${baseUrl}all-pharm-sale-gst/`,
                { params }
            );

            const data = getArray(response.data);

            // Preserve the existing Indent exclusion.
            const filteredData = data.filter(
                (item) => item.mode !== "Indent"
            );

            const formattedRows = filteredData.map(
                (item, index) => {
                    const saleDepartment = getSaleDepartment(
                        item,
                        departments
                    );

                    return {
                        ...item,

                        id: item.id ?? index,

                        // Display department name in Sale Type.
                        sale_type: saleDepartment,

                        // Same value is used by department filter.
                        department: saleDepartment,

                        consultant: getConsultant(item),

                        // Indent records are excluded above.
                        invoiceLink: `/pharm-invoice-other/${item.sale_id}`,

                        netamountValue: getNetAmount(item.netamount),
                    };
                }
            );

            setRows(formattedRows);
        } catch (err) {
            console.error("GST report fetch error:", err);

            setRows([]);

            setError(
                err.response?.data?.detail ||
                err.response?.data?.error ||
                "Unable to load GST sales report."
            );
        } finally {
            setIsLoading(false);
        }
    }, [startDate, endDate, addedby, mode, departments]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // =====================================================
    // DEPARTMENT OPTIONS
    // =====================================================

    const departmentOptions = useMemo(() => {
        const names = [
            ...departments.map((item) => item.department),
            ...rows.map((row) => row.department),
        ].filter(Boolean);

        return [...new Set(names)].sort((a, b) =>
            String(a).localeCompare(String(b))
        );
    }, [departments, rows]);

    // =====================================================
    // FILTER ROWS
    // =====================================================

    const filteredRows = useMemo(() => {
        return rows.filter((row) => {
            return (
                department === "all" ||
                normalize(row.department) === normalize(department)
            );
        });
    }, [rows, department]);

    // =====================================================
    // ROW ID
    // =====================================================

    const getRowId = (row) =>
        `${row.id}-${row.product}-${row.qty}-${row.batch}-${row.netamount}`;

    // =====================================================
    // DATA GRID COLUMNS
    // =====================================================

    const columns = useMemo(
        () => [
            {
                field: "add_date_date",
                headerName: "Date",
                width: 120,
            },
            {
                field: "add_time",
                headerName: "Time",
                width: 90,
            },
            {
                field: "sale_type",
                headerName: "Sale Type",
                width: 140,
            },
            {
                field: "pat__patient",
                headerName: "Patient",
                width: 150,
            },
            {
                field: "mode",
                headerName: "Mode",
                width: 100,
            },
            {
                field: "sale_id",
                headerName: "Invoice No",
                width: 130,
                renderCell: (params) => (
                    <Link
                        to={params.row.invoiceLink}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {params.row.sale_id}
                    </Link>
                ),
            },
            {
                field: "product",
                headerName: "Product",
                width: 200,
            },
            {
                field: "qty",
                headerName: "Qty",
                width: 80,
            },
            {
                field: "hsn",
                headerName: "HSN",
                width: 110,
            },
            {
                field: "gstPercent",
                headerName: "CGST %",
                width: 90,
                renderCell: (params) =>
                    formatAmount(params.row.gstPercent),
            },
            {
                field: "gstPercent1",
                headerName: "SGST %",
                width: 90,
                renderCell: (params) =>
                    formatAmount(params.row.gstPercent1),
            },
            {
                field: "discountedAmount",
                headerName: "Drug Amount",
                width: 120,
                renderCell: (params) => {
                    const value =
                        params.row.discountedAmount ??
                        params.row.gstTotal;

                    return formatAmount(value);
                },
            },
            {
                field: "disc",
                headerName: "Discount",
                width: 100,
                renderCell: (params) =>
                    formatAmount(params.row.disc),
            },
            {
                field: "gstAmountTotal",
                headerName: "CGST",
                width: 100,
                renderCell: (params) =>
                    formatAmount(params.row.gstAmountTotal),
            },
            {
                field: "gstAmountTotal1",
                headerName: "SGST",
                width: 100,
                renderCell: (params) =>
                    formatAmount(params.row.gstAmountTotal1),
            },
            {
                field: "netamountValue",
                headerName: "Net Amount",
                width: 150,
                type: "number",
                renderCell: (params) =>
                    formatAmount(params.row.netamountValue),
            },
            {
                field: "addedby",
                headerName: "Cashier",
                width: 100,
            },
        ],
        []
    );

    // =====================================================
    // CLEAR FILTERS
    // =====================================================

    const clearFilters = () => {
        setStartDate(null);
        setEndDate(null);
        setAddedby("");
        setMode("");
        setDepartment("all");
    };

    // =====================================================
    // SUMMARY TOTALS
    // =====================================================

    const summary = useMemo(() => {
        return {
            count: filteredRows.length,

            drugAmount: filteredRows.reduce(
                (total, row) =>
                    total +
                    toNumber(
                        row.discountedAmount ??
                        row.gstTotal
                    ),
                0
            ),

            discount: filteredRows.reduce(
                (total, row) => total + toNumber(row.disc),
                0
            ),

            cgst: filteredRows.reduce(
                (total, row) =>
                    total + toNumber(row.gstAmountTotal),
                0
            ),

            sgst: filteredRows.reduce(
                (total, row) =>
                    total + toNumber(row.gstAmountTotal1),
                0
            ),

            netAmount: filteredRows.reduce(
                (total, row) =>
                    total + getNetAmount(row.netamount),
                0
            ),
        };
    }, [filteredRows]);

    // =====================================================
    // UI
    // =====================================================

    const today = new Date();

    return (
        <section className="container mt-4">

            {/* PAGE TITLE */}
            <h3
                className="text-center"
                style={{
                    color: "white",
                    backgroundColor: "#0dcaf0",
                    padding: "8px",
                }}
            >
                <i
                    className="fa-solid fa-calendar-days"
                    aria-hidden="true"
                    style={{ color: "red" }}
                ></i>
                {" "}
                Detailed Sales GST Report
            </h3>

            {/* FILTERS */}
            <div className="row g-3 mb-3">

                {/* FROM DATE */}
                <div className="col-md-3">
                    <label className="form-label">
                        From Date
                    </label>

                    <DatePicker
                        selected={startDate}
                        onChange={setStartDate}
                        selectsStart
                        startDate={startDate}
                        endDate={endDate}
                        maxDate={today}
                        placeholderText="Select From Date"
                        dateFormat="dd/MM/yyyy"
                        showMonthDropdown
                        showYearDropdown
                        isClearable
                        className="form-control"
                    />
                </div>

                {/* TO DATE */}
                <div className="col-md-3">
                    <label className="form-label">
                        To Date
                    </label>

                    <DatePicker
                        selected={endDate}
                        onChange={setEndDate}
                        selectsEnd
                        startDate={startDate}
                        endDate={endDate}
                        minDate={startDate || undefined}
                        maxDate={today}
                        placeholderText="Select To Date"
                        dateFormat="dd/MM/yyyy"
                        showMonthDropdown
                        showYearDropdown
                        isClearable
                        className="form-control"
                    />
                </div>

                {/* CASHIER */}
                <div className="col-md-2">
                    <label
                        htmlFor="username"
                        className="form-label"
                    >
                        Cashier
                    </label>

                    <select
                        id="username"
                        value={addedby}
                        className="form-control"
                        onChange={(e) => setAddedby(e.target.value)}
                    >
                        <option value="">All Cashiers</option>

                        {userData.map((user, index) => (
                            <option
                                key={user.id ?? index}
                                value={user.username}
                            >
                                {user.username}
                            </option>
                        ))}
                    </select>
                </div>

                {/* PAYMENT MODE */}
                <div className="col-md-2">
                    <label
                        htmlFor="mode"
                        className="form-label"
                    >
                        Payment Mode
                    </label>

                    <select
                        id="mode"
                        value={mode}
                        className="form-control"
                        onChange={(e) => setMode(e.target.value)}
                    >
                        <option value="">All Modes</option>
                        <option value="cash">Cash</option>
                        <option value="card">Card</option>
                        <option value="creditpay">Credit Payment</option>
                        <option value="advance">Advance</option>
                        <option value="UPI">UPI</option>
                    </select>
                </div>

                {/* DEPARTMENT */}
                <div className="col-md-2">
                    <label
                        htmlFor="department"
                        className="form-label"
                    >
                        Department
                    </label>

                    <select
                        id="department"
                        value={department}
                        className="form-control"
                        onChange={(e) =>
                            setDepartment(e.target.value)
                        }
                    >
                        <option value="all">All Departments</option>

                        {departmentOptions.map((name) => (
                            <option key={name} value={name}>
                                {name}
                            </option>
                        ))}
                    </select>
                </div>

                {/* ACTIONS */}
                <div className="col-12 d-flex justify-content-end gap-2">
                    <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={clearFilters}
                    >
                        Clear Filters
                    </button>

                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={fetchData}
                        disabled={isLoading}
                    >
                        Refresh
                    </button>
                </div>
            </div>

            {/* SUMMARY */}
            <div className="row g-2 mb-3">

                <div className="col-md-2 col-6">
                    <div className="card p-2 h-100">
                        <small className="text-muted">Total Records</small>
                        <h5 className="mb-0">{summary.count}</h5>
                    </div>
                </div>

                <div className="col-md-2 col-6">
                    <div className="card p-2 h-100">
                        <small className="text-muted">Drug Amount</small>
                        <h6 className="mb-0">
                            ₹ {formatAmount(summary.drugAmount)}
                        </h6>
                    </div>
                </div>

                <div className="col-md-2 col-6">
                    <div className="card p-2 h-100">
                        <small className="text-muted">Discount</small>
                        <h6 className="mb-0">
                            ₹ {formatAmount(summary.discount)}
                        </h6>
                    </div>
                </div>

                <div className="col-md-2 col-6">
                    <div className="card p-2 h-100">
                        <small className="text-muted">CGST</small>
                        <h6 className="mb-0">
                            ₹ {formatAmount(summary.cgst)}
                        </h6>
                    </div>
                </div>

                <div className="col-md-2 col-6">
                    <div className="card p-2 h-100">
                        <small className="text-muted">SGST</small>
                        <h6 className="mb-0">
                            ₹ {formatAmount(summary.sgst)}
                        </h6>
                    </div>
                </div>

                <div className="col-md-2 col-6">
                    <div className="card p-2 h-100">
                        <small className="text-muted">Net Amount</small>
                        <h6 className="mb-0">
                            ₹ {formatAmount(summary.netAmount)}
                        </h6>
                    </div>
                </div>

            </div>

            {/* ERROR */}
            {error && (
                <div className="alert alert-danger">
                    {error}
                </div>
            )}

            {/* DATA GRID */}
            <div
                style={{
                    width: "100%",
                    overflowX: "auto",
                }}
            >
                <div style={{ minWidth: "1990px" }}>
                    <DataGrid
                        className="mt-3"
                        sx={{
                            boxShadow: 3,
                            border: 1,
                            borderColor: "primary.light",
                            "& .MuiDataGrid-columnHeaders": {
                                backgroundColor: "#f5f5f5",
                                fontWeight: "bold",
                            },
                            "& .MuiDataGrid-cell": {
                                fontSize: "13px",
                            },
                        }}
                        getRowId={getRowId}
                        rows={filteredRows}
                        columns={columns}
                        slots={{
                            toolbar: GridToolbar,
                        }}
                        initialState={{
                            pagination: {
                                paginationModel: {
                                    pageSize: 100,
                                    page: 0,
                                },
                            },
                        }}
                        pageSizeOptions={[25, 50, 100, 200]}
                        autoHeight
                        loading={isLoading}
                        disableRowSelectionOnClick
                        density="compact"
                    />

                    {/* TOTAL FOOTER */}
                    <GstReportFooter rows={filteredRows} />
                </div>
            </div>

        </section>
    );
}

export default DetailSalesGstReport;
